'use client';

import { isEthereumWallet } from '@dynamic-labs/ethereum';
import {
  useDynamicContext,
  useSwitchWallet,
  useUserWallets,
} from '@dynamic-labs/sdk-react-core';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createWalletClient, custom, WalletClient, zeroAddress } from 'viem';
import { base, baseSepolia, mainnet, sepolia } from 'viem/chains';

import { EvmNFTChain } from '@repo/types-client';

import { returnChain, withLock } from '../../utils';

/**
 * Options for configuring the EVM wallet hook behavior.
 *
 * @interface Options
 * @property {boolean} [switchWallet] - Whether to automatically switch the active wallet
 *                                     when resolving a client for a specific address
 */
type Options = {
  switchWallet?: boolean;
};

/**
 * Props for the useEvmWallet hook.
 *
 * @interface Props
 * @property {EvmNFTChain | number} [chain] - The blockchain network to connect to
 * @property {Options} [options] - Configuration options for the hook
 * @property {boolean} [auto] - Whether to automatically resolve the wallet client on mount
 * @property {string} [address] - Specific wallet address to connect to
 */
type Props = {
  chain?: EvmNFTChain | number;
  options?: Options;
  auto?: boolean;
  address?: string;
};

/**
 * Result type returned by the useEvmWallet hook.
 *
 * This type defines the structure of the object returned by the useEvmWallet hook,
 * providing access to the wallet client, chain ID, and a function to manually
 * resolve the wallet client.
 *
 * @interface EvmWalletHookResult
 * @property {WalletClient | undefined} walletClient - The viem wallet client instance
 *                                                    for interacting with the blockchain.
 *                                                    Undefined if no wallet is connected.
 * @property {number | undefined} chainId - The numeric ID of the connected blockchain network.
 *                                         Undefined if no network is connected.
 * @property {Function} resolveWalletClient - Function to manually resolve a wallet client
 *                                           for a specific chain and address.
 */
export type EvmWalletHookResult = {
  walletClient: WalletClient | undefined;
  chainId: number | undefined;
  resolveWalletClient: (
    chain: EvmNFTChain | number,
    address?: string,
    options?: { switchWallet?: boolean },
  ) => Promise<WalletClient | undefined>;
};

/**
 * Custom hook for managing EVM wallet connections.
 *
 * This hook provides functionality to:
 * - Connect to EVM-compatible blockchains (Ethereum, Base, etc.)
 * - Switch between different wallet addresses
 * - Manage network switching
 * - Create and provide a viem WalletClient for blockchain interactions
 *
 * It integrates with Dynamic's wallet management system and handles
 * various edge cases like non-EVM wallets, wallet switching, and
 * network switching.
 *
 * @template T - The return type of the hook
 * @param {Props} props - Configuration props for the hook
 * @returns {T} An object containing the wallet client, chain ID, and a function to resolve the wallet client
 *
 */
export const useEvmWallet = <T extends EvmWalletHookResult>({
  chain,
  address,
  auto,
  options,
}: Props): T => {
  // Get wallet context from Dynamic
  const { primaryWallet } = useDynamicContext();
  const internalSwitchWallet = useSwitchWallet();
  const userWallets = useUserWallets();

  // Use refs to access the latest values in callbacks
  const primaryWalletRef = useRef(primaryWallet);
  const userWalletsRef = useRef(userWallets);
  const internalSwitchWalletRef = useRef(internalSwitchWallet);

  // State for the wallet client and chain ID
  const [walletClient, setWalletClient] = useState<WalletClient | undefined>(
    undefined,
  );
  const [chainId, setChainId] = useState<number | undefined>(undefined);

  // Keep refs updated with the latest values
  useEffect(() => {
    primaryWalletRef.current = primaryWallet;
    userWalletsRef.current = userWallets;
    internalSwitchWalletRef.current = internalSwitchWallet;
  }, [primaryWallet, userWallets, internalSwitchWallet]);

  /**
   * Resolves a wallet client for the specified chain and address.
   *
   * This function:
   * 1. Finds the appropriate wallet based on the address
   * 2. Switches to that wallet if needed
   * 3. Ensures the wallet is connected to the correct network
   * 4. Creates and returns a viem WalletClient
   *
   * The function uses a lock mechanism to prevent concurrent executions
   * for the same chain and address combination.
   *
   * @param {EvmNFTChain | number} chain - The blockchain network to connect to
   * @param {string | undefined} address - Specific wallet address to connect to
   * @param {Options} [options] - Configuration options
   * @returns {Promise<WalletClient | undefined>} The resolved wallet client or undefined if resolution fails
   */
  const resolveClient = useCallback(
    async (
      chain: EvmNFTChain | number,
      address: string | undefined,
      options?: Options,
    ) => {
      // Treat zero address as undefined
      if (address === zeroAddress) {
        address = undefined;
      }

      // Create a unique lock key based on chain and address
      const lockKey = `${chain}:${address || 'primary'}`.toLowerCase();

      return withLock(
        lockKey,
        async () => {
          // Convert chain name to chain ID if needed
          const chainId =
            typeof chain === 'number' ? chain : returnChain(chain)!;

          // Determine if we should switch the wallet context
          const switchWalletContext = options?.switchWallet !== false;

          let contextChanged = false;

          // Find the appropriate wallet to use
          let currentWallet =
            primaryWalletRef.current ||
            userWalletsRef.current.find(
              (x) =>
                !address || x.address.toLowerCase() === address.toLowerCase(),
            );

          // If current wallet is not an Ethereum wallet, find the best EVM wallet
          if (currentWallet && !isEthereumWallet(currentWallet)) {
            // Get all EVM wallets with their balances
            const evmWallets = await Promise.all(
              userWalletsRef.current
                .filter((x) => isEthereumWallet(x))
                .map(async (x) => {
                  let balance: string | undefined;
                  try {
                    balance = await x.getBalance();
                  } catch (e) {
                    balance = undefined;
                  }
                  return {
                    wallet: x,
                    balance: new BigNumber(balance || 0),
                  };
                }),
            );

            // Choose the wallet with the highest balance
            currentWallet = evmWallets.sort((a, b) =>
              a.balance.isGreaterThan(b.balance) ? -1 : 1,
            )[0]?.wallet;
            contextChanged = true;
          }

          // If a specific address is requested and it's not the current wallet
          if (
            currentWallet &&
            address &&
            address.toLowerCase() !== currentWallet.address.toLowerCase()
          ) {
            // Find the wallet with the requested address
            const wallet = userWalletsRef.current.find(
              (x) => x.address.toLowerCase() === address!.toLowerCase(),
            );
            if (!wallet) return;
            contextChanged = true;
            currentWallet = wallet;
          }

          // Switch to the selected wallet if needed
          if (currentWallet && contextChanged && switchWalletContext) {
            await internalSwitchWalletRef.current(currentWallet.id);
          }

          // Update the chain ID state
          setChainId(chainId);

          // If we have a valid Ethereum wallet, use it
          if (currentWallet && isEthereumWallet(currentWallet)) {
            // Switch to the correct network
            await currentWallet.switchNetwork(chainId);
            // Get the wallet client from Dynamic
            const client = await currentWallet.getWalletClient();
            setWalletClient(client);
            return client;
          }

          // Fallback to window.ethereum if no Dynamic wallet is available
          const provider = (window as any)?.ethereum;
          if (!provider) return;

          // Request accounts from the provider
          const accounts: `0x${string}`[] = await provider.request({
            method: 'eth_requestAccounts',
          });

          // Find the account matching the requested address
          const account = accounts.find(
            (x) => !address || x.toLowerCase() === address.toLowerCase(),
          );
          if (!account) return;

          // Create a viem wallet client with the provider
          const chains = [base, baseSepolia, mainnet, sepolia];
          const client = createWalletClient({
            chain: chains.find((x) => x.id === chainId),
            transport: custom(provider),
            account,
          });

          setWalletClient(client);
          return client;
        },
        {
          defaultValue: undefined,
          cache: false,
        },
      );
    },
    [],
  );

  // Automatically resolve the wallet client on mount if auto is not false
  useEffect(() => {
    if (!chain || !primaryWallet || auto === false) return;
    resolveClient(chain, address, options);
  }, [auto, primaryWallet, chain, address, options, resolveClient]);

  // Return the wallet client, chain ID, and resolve function
  return {
    walletClient,
    chainId,
    resolveWalletClient: resolveClient,
  } as T;
};
